import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { toast as toastFn } from "@/hooks/use-toast";
import { MASTER_ADMIN_IMPERSONATION_PHRASE } from "@/lib/permission-errors";
import { installFetchImpersonationGuard } from "@/lib/fetch-impersonation-guard";
import { advancePersonalCacheBoundary } from "@/lib/personal-cache-boundary";

// Install the global fetch interceptor so ALL same-origin 403 responses
// containing the master-admin impersonation phrase dispatch the event — even
// from admin-panel pages that use raw fetch() instead of apiRequest.
installFetchImpersonationGuard();

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    
    // Try to parse JSON error response
    let errorMessage = text || res.statusText;
    let parsedData: any = null;
    try {
      parsedData = JSON.parse(text);
      // Build a helpful error message from available fields
      const parts = [];
      if (parsedData.error) parts.push(parsedData.error);
      if (parsedData.message && parsedData.message !== parsedData.error) parts.push(parsedData.message);
      if (parsedData.details) {
        const details = typeof parsedData.details === "string"
          ? parsedData.details
          : JSON.stringify(parsedData.details);
        parts.push(`Details: ${details}`);
      }
      errorMessage = parts.length > 0 ? parts.join(' - ') : text;
    } catch {
      // If not JSON, use raw text
    }

    // Fire a global event for plan limit errors (402) so a single dialog can handle them everywhere
    if (res.status === 402) {
      const limitMessage = parsedData?.message || parsedData?.error || "You have reached your plan limit.";
      window.dispatchEvent(new CustomEvent("plan-limit-exceeded", { detail: { message: limitMessage } }));
    }

    // Note: the "master-admin-impersonation-blocked" event for 403 responses is
    // dispatched exclusively by the global fetch interceptor installed in
    // fetch-impersonation-guard.ts (via installFetchImpersonationGuard), which
    // covers both apiRequest callers and raw fetch() callers in admin pages.
    // Do NOT duplicate the dispatch here — the interceptor already fires before
    // throwIfResNotOk runs, so a second dispatch would show the toast twice.

    const error = new Error(errorMessage);
    // Attach the HTTP status so callers (e.g. react-query retry predicates
    // and "not found" UI states) can branch on it.
    (error as Error & { status?: number }).status = res.status;
    // Attach the server's skippedSubTaskIds (sent by the sub-task reorder
    // endpoint on a 403 when a share was downgraded from edit to view
    // mid-session) so the UI can show a specific "edit access removed"
    // message instead of a generic error.
    if (parsedData && Array.isArray(parsedData.skippedSubTaskIds)) {
      (error as Error & { skippedSubTaskIds?: string[] }).skippedSubTaskIds =
        parsedData.skippedSubTaskIds;
    }
    // Same signal from the milestone reorder endpoint: skippedMilestoneIds on
    // a 403 marks a mid-session edit-to-view downgrade.
    if (parsedData && Array.isArray(parsedData.skippedMilestoneIds)) {
      (error as Error & { skippedMilestoneIds?: string[] }).skippedMilestoneIds =
        parsedData.skippedMilestoneIds;
    }
    if (parsedData && typeof parsedData.code === "string") {
      (error as Error & { code?: string }).code = parsedData.code;
    }
    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  extraHeaders?: Record<string, string>,
  requestOptions?: Pick<RequestInit, "signal">,
): Promise<Response> {
  const res = await fetch(url, {
    ...requestOptions,
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...extraHeaders,
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    // Disable browser caching to prevent 304 responses
    cache: "no-store",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      // Disable browser caching to prevent 304 responses which can cause issues
      // when the server responds with 304 but the fetch wrapper expects a body
      cache: "no-store",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Do not keep another session's private records in the shared query cache.
// Leave the auth query intact: removing it during its own fetch would discard
// the new principal and cause an extra auth request.
export function clearPersonalQueryCaches() {
  advancePersonalCacheBoundary();
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== "/api/auth/user",
  });
}

// Several screens also observe /api/auth/user with their own query options.
// Watch the shared cache rather than relying on a particular useAuth queryFn
// being selected for a refetch. This runs synchronously on a successful auth
// write, before subscribers can render the newly authenticated person.
let lastPrincipal: string | undefined;
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "removed" && event.query.queryKey[0] === "/api/auth/user") {
    advancePersonalCacheBoundary();
    return;
  }
  if (
    event.type !== "updated" ||
    event.action.type !== "success" ||
    event.query.queryKey[0] !== "/api/auth/user"
  ) return;
  const response = event.query.state.data as
    | { user?: { id?: string }; tenant?: { id?: string } }
    | null
    | undefined;
  const principal = JSON.stringify([response?.user?.id ?? null, response?.tenant?.id ?? null]);
  if (lastPrincipal !== undefined && lastPrincipal !== principal) {
    clearPersonalQueryCaches();
  }
  lastPrincipal = principal;
});

// Surface a task mutation failure to the user. Server-side rejections (e.g.
// the 403 "You don't have edit permission on this shared task" returned to
// view-only share recipients) carry a meaningful message that must not be
// swallowed or replaced with a generic one.
export function toastTaskMutationError(title: string, error: unknown, fallback: string) {
  const message = error instanceof Error && error.message ? error.message : fallback;
  toastFn({ title, description: message, variant: "destructive" });
}
